import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, CheckCircle2, Sparkles, HelpCircle, AlertTriangle, Filter,
} from 'lucide-react';
import CategorySelect from '@/components/shared/CategorySelect';

/**
 * Review screen shown after a CSV is parsed and matched.
 *
 * Every row is editable before saving, regardless of how confident the match
 * was — a green "exact" match can still be wrong, so it gets the same picker
 * as everything else.
 *
 * Rows are colour-coded by how the category was decided, because the tiers
 * are NOT equally trustworthy: exact and merchant matches come from identical
 * previous transactions, fuzzy matches are guesses.
 */

const CONFIDENCE_META = {
  exact:  { label: 'Exact',    icon: CheckCircle2, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  high:   { label: 'Merchant', icon: CheckCircle2, cls: 'text-teal-600 bg-teal-50 border-teal-200' },
  medium: { label: 'Similar',  icon: Sparkles,     cls: 'text-amber-600 bg-amber-50 border-amber-200' },
  none:   { label: 'No match', icon: HelpCircle,   cls: 'text-slate-500 bg-slate-50 border-slate-200' },
};

// ---------------------------------------------------------------------------

export default function ImportReviewModal({
  matched,          // [{...txn, match:{category, confidence, reason, alternatives}}]
  categories,       // all known category strings
  duplicateCount,   // how many rows were skipped as duplicates
  onConfirm,        // (finalTransactions) => void
  onCancel,
  isSaving,
}) {
  const [overrides, setOverrides] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | needs-review | none
  // Only one category menu may be open at a time. Keeping this in the parent
  // is what prevents menus stacking on top of each other.
  const [openMenuIdx, setOpenMenuIdx] = useState(null);

  const counts = useMemo(() => {
    const c = { exact: 0, high: 0, medium: 0, none: 0 };
    matched.forEach(t => c[t.match.confidence]++);
    return c;
  }, [matched]);

  const categoryFor = (idx) =>
    overrides[idx] !== undefined ? overrides[idx] : matched[idx].match.category;

  const setCategory = (idx, value) =>
    setOverrides(o => ({ ...o, [idx]: value }));

  // Apply one category to every row sharing the same match reason — the fast
  // path for a merchant that appears many times in a single import.
  const applyToSimilar = (idx, value) => {
    const key = matched[idx].match.reason;
    setOverrides(prev => {
      const next = { ...prev };
      matched.forEach((t, i) => {
        if (t.match.reason === key) next[i] = value;
      });
      return next;
    });
  };

  // Categories offered in the picker: everything from history, plus anything
  // the matcher proposed or you've typed during this review.
  const pickerCategories = useMemo(() => {
    const set = new Set(categories);
    matched.forEach(t => { if (t.match.category) set.add(t.match.category); });
    Object.values(overrides).forEach(v => { if (v) set.add(v); });
    return [...set].sort();
  }, [categories, matched, overrides]);

  const visibleIndexes = useMemo(() => {
    return matched
      .map((_, i) => i)
      .filter(i => {
        const t = matched[i];
        if (filter === 'needs-review' && !['medium', 'none'].includes(t.match.confidence)) return false;
        if (filter === 'none' && t.match.confidence !== 'none') return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          t.description.toLowerCase().includes(q) ||
          (categoryFor(i) || '').toLowerCase().includes(q)
        );
      });
  }, [matched, search, filter, overrides]);

  const uncategorisedCount = matched.filter((_, i) => !categoryFor(i)).length;
  const editedCount = Object.keys(overrides).filter(
    i => overrides[i] !== matched[i].match.category
  ).length;

  const handleConfirm = () => {
    const final = matched.map((t, i) => {
      const { match: _match, ...txn } = t;
      return { ...txn, category: categoryFor(i) || '' };
    });
    onConfirm(final);
  };

  const fmt = (v) =>
    '$' + (v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-4xl flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-base">
            Review import
            <span className="ml-2 text-sm font-normal text-slate-400">
              {matched.length.toLocaleString()} transaction{matched.length !== 1 ? 's' : ''}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Confidence summary */}
        <div className="grid grid-cols-4 gap-2">
          {Object.entries(CONFIDENCE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <div key={key} className={`rounded-lg border px-3 py-2 ${meta.cls}`}>
                <div className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">{meta.label}</span>
                </div>
                <p className="text-lg font-bold leading-tight mt-0.5">{counts[key]}</p>
              </div>
            );
          })}
        </div>

        {duplicateCount > 0 && (
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            {duplicateCount.toLocaleString()} row{duplicateCount !== 1 ? 's were' : ' was'} already in
            your history and won&apos;t be imported again.
          </p>
        )}

        {(counts.medium > 0 || counts.none > 0) && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Similar</strong> matches are fuzzy guesses based on partial description
              overlap — worth checking. <strong>Exact</strong> and <strong>Merchant</strong>{' '}
              matches come from identical previous transactions. Any row can be changed.
            </span>
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-9 h-9 text-sm"
              placeholder="Search description or category..."
              value={search}
              onChange={e => { setSearch(e.target.value); setOpenMenuIdx(null); }}
            />
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { key: 'all', label: 'All' },
              { key: 'needs-review', label: 'Needs review' },
              { key: 'none', label: 'Unmatched' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => { setFilter(f.key); setOpenMenuIdx(null); }}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  filter === f.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {visibleIndexes.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400">
              <Filter className="w-5 h-5 mx-auto mb-2 text-slate-300" />
              Nothing matches that filter
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white border-b border-slate-100 z-10">
                <tr className="text-xs text-slate-400 uppercase tracking-wide">
                  <th className="text-left py-2 px-2 font-medium">Date</th>
                  <th className="text-left py-2 px-2 font-medium">Description</th>
                  <th className="text-right py-2 px-2 font-medium">Amount</th>
                  <th className="text-left py-2 px-2 font-medium w-60">Category</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {visibleIndexes.map(i => {
                  const t = matched[i];
                  const meta = CONFIDENCE_META[t.match.confidence];
                  const Icon = meta.icon;
                  const value = categoryFor(i);
                  const edited = overrides[i] !== undefined && overrides[i] !== t.match.category;

                  return (
                    <tr key={i} className="hover:bg-slate-50/60 align-top">
                      <td className="py-2 px-2 text-slate-400 whitespace-nowrap text-xs">{t.date}</td>
                      <td className="py-2 px-2">
                        <p className="text-slate-700 leading-snug">{t.description}</p>
                        <span className={`inline-flex items-center gap-1 mt-1 text-xs px-1.5 py-0.5 rounded border ${meta.cls}`}>
                          <Icon className="w-3 h-3" />
                          {t.match.reason}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <span className={`font-medium ${t.type === 'debit' ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {t.type === 'debit' ? '-' : '+'}{fmt(t.amount)}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <CategorySelect
                          value={value}
                          categories={pickerCategories}
                          onChange={v => setCategory(i, v)}
                          highlight={edited}
                          isOpen={openMenuIdx === i}
                          onOpenChange={o => setOpenMenuIdx(o ? i : null)}
                        />
                        <div className="flex flex-wrap gap-1 mt-1">
                          {t.match.alternatives?.slice(0, 2).map(alt => (
                            <button
                              key={alt}
                              onClick={() => setCategory(i, alt)}
                              className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                              title="Also used for this merchant previously"
                            >
                              {alt}
                            </button>
                          ))}
                          {value && (
                            <button
                              onClick={() => applyToSimilar(i, value)}
                              className="text-xs px-1.5 py-0.5 rounded text-emerald-600 hover:bg-emerald-50"
                              title="Apply this category to every row with the same match"
                            >
                              apply to similar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-400">
            {editedCount > 0 && (
              <span className="text-emerald-600 font-medium">{editedCount} changed · </span>
            )}
            {uncategorisedCount > 0
              ? `${uncategorisedCount} will be imported without a category`
              : 'Every transaction has a category'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleConfirm}
              disabled={isSaving}
            >
              {isSaving ? 'Importing…' : `Import ${matched.length.toLocaleString()}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
